import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';

import { isNotEmpty, scrollToAnchor } from '~/utils';
import { appointmentDraftActions, journeyStateActions } from '~/api';
import {
  loadFittedBedroomsSlots,
  setFilters,
  setSlots,
  replaceFilters,
} from '~/routes/select-date/select-date.actions';
import { dateUtils } from '~/routes/utils';
import {
  CalendarWithFilters,
  eligibleSlotsFilter as defaultEligibleSlotsFilter,
  errorMessagesActions,
  errorTypes,
  Messages,
  scrollToMessageTypeAnchor,
  apiCallFinished,
  EditAppointmentTitle,
  StateContext,
  states,
  withRestorableDraft,
  calendarSelectors,
  warningModalSelectors,
} from '~/components';
import {
  saveTemporaryAppointment,
  setEditMode,
  replaceTimeFilters,
  saveTemporaryJourneyState,
} from '../../edit-appointment.actions';
import {
  triggerEditPageChangesMadeAnalytics,
  triggerEditPageSaveEditsAnalytics,
} from '~/analytics/analytics.listener';

import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';
import { formatToTemporaryState } from '../../helpers';

import {
  getTokenFromStorage,
  removeTokenFromStorage,
  TEMPORARY_TOKEN_NAME,
} from '~/components/persist-token/persist-token.utils';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ title, dataTest }) => (
  <h2 data-testid={dataTest}>{title}</h2>
));
const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR } = errorTypes;

const getEligibleSlotsFilter = (config) =>
  config.Calendar && config.Calendar.eligibleSlotsFilter
    ? config.Calendar.eligibleSlotsFilter
    : defaultEligibleSlotsFilter;

export class FittedBedroomsEditDate extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  componentDidMount() {
    const { temporaryAppointment, dispatchApiCallFinished } = this.props;

    dispatchApiCallFinished(false);

    if (isNotEmpty(temporaryAppointment)) {
      this.loadSlots();
    }
    this.saveOriginalData();

    window.scrollTo(0, 0);
  }

  componentDidUpdate({ selectedDateSlot: prevSelectedDateSlot }) {
    const {
      selectedDateSlot,
      selectedTimeSlot,
      slotErrorMessages,

      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && selectedDateSlot.times.length) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && selectedTimeSlot.from) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (prevSelectedDateSlot.date === '' && selectedDateSlot.date) {
      scrollToAnchor('time-slot-title');
    }
  }

  saveOriginalData = () => {
    const { filters, originalFilters, dispatchReplaceTimeFilters } = this.props;

    if (!originalFilters.length) {
      dispatchReplaceTimeFilters(filters);
    }
  };

  loadSlots = () => {
    const {
      dispatchLoadFittedBedroomsSlots,
      dispatchApiCallFinished,
      journeyState: { searchQuery: postcode },
      config,
      selectedDateSlot,
    } = this.props;

    const startDate = dateUtils.getSlotsStartDate(selectedDateSlot.date);

    dispatchLoadFittedBedroomsSlots(
      postcode,
      startDate,
      getEligibleSlotsFilter(config),
      getTokenFromStorage(TEMPORARY_TOKEN_NAME),
    ).then(() => {
      dispatchApiCallFinished(true);
      this.setState({
        isAjaxCompleted: true,
      });
    });
  };

  saveHandler = () => {
    const {
      selectedDateSlot: { date },
      selectedTimeSlot: { from: time },
      onSaveDataHandler,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    if (!date) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (!time) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      return onSaveDataHandler()
        .then(() => dispatchApiCallFinished(true))
        .then(() => this.exitEditMode())
        .then(() => triggerEditPageSaveEditsAnalytics('appointments'));
    }
    return null;
  };

  selectDateSlot = (selectedDateSlot) => {
    const { dispatchSelectDateSlot, dispatchRemoveSingleErrorMessage } = this.props;
    if (selectedDateSlot) {
      dispatchSelectDateSlot(selectedDateSlot.date);
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    } else {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      dispatchSelectDateSlot(null);
    }
    triggerEditPageChangesMadeAnalytics('appointments', 'appointments:date/time');
  };

  selectTimeSlot = (selectedTimeSlot) => {
    const { dispatchSelectTimeSlot, dispatchRemoveSingleErrorMessage } = this.props;
    if (selectedTimeSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }
    dispatchSelectTimeSlot(selectedTimeSlot);
    triggerEditPageChangesMadeAnalytics('appointments', 'appointments:date/time');
  };

  exitEditMode = () => {
    const {
      history,
      dispatchSetEditMode,
      dispatchSaveTemporaryAppointment,
      dispatchReplaceTimeFilters,
      dispatchSaveTemporaryJourneyState,
    } = this.props;
    const {
      location: { pathname },
    } = history;

    dispatchSetEditMode(false);
    dispatchSaveTemporaryAppointment({});
    dispatchSaveTemporaryJourneyState({});
    dispatchReplaceTimeFilters([]);
    removeTokenFromStorage(TEMPORARY_TOKEN_NAME);
    const match = matchPath(pathname, { path: '/:appointmentId([0-9]+)' }) || { params: {} };
    history.pushWithToken(
      match.params.appointmentId ? `/${match.params.appointmentId}` : '/review',
    );
  };

  onCancelHandler = () => {
    const { originalFilters, dispatchReplaceFilters, dispatchReplaceTimeFilters } = this.props;

    dispatchReplaceTimeFilters([]);
    dispatchReplaceFilters(originalFilters);
  };

  render() {
    const {
      draft,
      journeyState,
      slots,
      selectedDateSlot,
      selectedTimeSlot,
      dispatchApiCallFinished,
      dispatchApplyFilters,
      filters,
      config: { Calendar = {} },
      temporaryAppointment,
      history,
      dispatchSetEditMode,
      dispatchSetDraftAppointment,
      dispatchSaveTemporaryAppointment,
      prevPath,
      editMode,
      temporaryJourneyState,
      dispatchSetJourneyState,
      location,
      showWarningModal,
    } = this.props;

    const {
      calendarTimeSlotsIntro: CalendarTimeSlotsIntro,
      timeSlotsList,
      eligibleSlotsFilter = defaultEligibleSlotsFilter,
      formatTimeSlots,
    } = Calendar;
    const { isAjaxCompleted } = this.state;

    const {
      appointmentType: { maxAdvance },
      shop: { name: selectedShopName },
      id: bookingId,
    } = draft;

    return (
      <section data-testid="EditDate" data-test-ajax={isAjaxCompleted}>
        <EditAppointmentTitle />
        <ScrollToMessageTypeAnchor
          dataTest="EditDateTitle"
          title="Choose a date for your appointment"
          forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}
        />
        {slots.length ? (
          <CalendarWithFilters
            filters={filters}
            slots={slots}
            selectedDateSlot={selectedDateSlot}
            selectedTimeSlot={selectedTimeSlot}
            timeSlotsList={timeSlotsList}
            applyFiltersHandler={dispatchApplyFilters}
            selectDateSlot={this.selectDateSlot}
            selectTimeSlot={this.selectTimeSlot}
            extraClassName="SelectDateBordered"
            apiCallFinished={dispatchApiCallFinished}
            maxAdvance={maxAdvance}
            eligibleSlotsFilter={eligibleSlotsFilter}
            editMode={editMode}
            formatTimeSlots={formatTimeSlots}
          >
            <Messages forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]} />
            <Messages forTypes={[SLOT_TIME_NOT_SELECTED_ERROR]} />
            {CalendarTimeSlotsIntro && <CalendarTimeSlotsIntro shopName={selectedShopName} />}
          </CalendarWithFilters>
        ) : null}
        <StateContext.Provider value={states.edit}>
          <EditButtonsWrapper
            temporaryDraftAppointment={temporaryAppointment}
            history={history}
            location={location}
            showWarningModal={showWarningModal}
            bookingId={bookingId}
            state={formatToTemporaryState(draft)}
            journeyState={journeyState}
            saveHandler={this.saveHandler}
            setEditMode={dispatchSetEditMode}
            onCancelHandler={this.onCancelHandler}
            saveTemporaryState={dispatchSaveTemporaryAppointment}
            saveButtonText="Save"
            prevPath={prevPath}
            temporaryJourneyState={temporaryJourneyState}
            restoreAppointmentDraft={dispatchSetDraftAppointment}
            restoreJourneyState={dispatchSetJourneyState}
          />
        </StateContext.Provider>
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    appointment: { draft },
    journeyState,
    selectDate: { slots, filters },
    services: { config },
    editAppointment: { temporaryAppointment, editMode, timeFilters },
    messages: {
      app: { errorMessages: slotErrorMessages },
    },
    router: { location },
  } = state;
  const { selectSlotsByFilters, selectDateSlot, selectTimeSlot, selectSlotMessages } =
    calendarSelectors;

  const selectedSlots = selectSlotsByFilters(slots, filters, getEligibleSlotsFilter(config));
  const selectedDateSlot = selectDateSlot(selectedSlots, draft.dateSlot);
  const selectedTimeSlot = selectTimeSlot(selectedDateSlot, draft.timeSlot);
  const selectedSlotErrorMessages = selectSlotMessages(slotErrorMessages);
  const warningModalState = warningModalSelectors.selectState(state);

  return {
    draft,
    journeyState,
    slots: selectedSlots,
    filters,
    originalFilters: timeFilters,
    config,
    temporaryAppointment,
    editMode,
    prevPath: config.editDatePrevPath,
    selectedDateSlot,
    selectedTimeSlot,
    slotErrorMessages: selectedSlotErrorMessages,
    location,
    showWarningModal: !!warningModalState.isOpen,
  };
};

const mapDispatchToProps = {
  dispatchLoadFittedBedroomsSlots: loadFittedBedroomsSlots,
  dispatchSetSlots: setSlots,
  dispatchSelectDateSlot: appointmentDraftActions.setDateSlot,
  dispatchSelectTimeSlot: appointmentDraftActions.setTimeSlot,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchApplyFilters: setFilters,
  dispatchApiCallFinished: apiCallFinished,
  dispatchSetEditMode: setEditMode,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchReplaceTimeFilters: replaceTimeFilters,
  dispatchReplaceFilters: replaceFilters,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
  dispatchSaveTemporaryJourneyState: saveTemporaryJourneyState,
};

export const ConnectedFittedBedroomsEditDate = withRestorableDraft(
  connect(mapStateToProps, mapDispatchToProps)(FittedBedroomsEditDate),
);

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

const dateSlotModel = {
  date: PropTypes.string.isRequired,
  times: PropTypes.arrayOf(PropTypes.shape(timeSlotModel)),
};

FittedBedroomsEditDate.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  prevPath: PropTypes.string.isRequired,
  filters: PropTypes.arrayOf(PropTypes.string),
  draft: PropTypes.shape({
    appointmentType: PropTypes.shape({
      maxAdvance: PropTypes.number.isRequired,
    }).isRequired,
    shop: PropTypes.shape({}).isRequired,
    dateSlot: PropTypes.string,
    timeSlot: PropTypes.shape({}),
    id: PropTypes.string,
  }).isRequired,
  journeyState: PropTypes.shape({}).isRequired,
  temporaryJourneyState: PropTypes.shape({}).isRequired,
  temporaryAppointment: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  originalFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedDateSlot: PropTypes.shape(dateSlotModel),
  selectedTimeSlot: PropTypes.shape(timeSlotModel),
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,

  config: PropTypes.shape({
    firstEditPagePath: PropTypes.string,
    Calendar: PropTypes.shape({
      calendarTimeSlotsIntro: PropTypes.func,
      timeSlotsList: PropTypes.func,
      eligibleSlotsFilter: PropTypes.func,
      formatTimeSlots: PropTypes.func,
    }),
  }).isRequired,
  editMode: PropTypes.bool.isRequired,
  onSaveDataHandler: PropTypes.func.isRequired,

  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadFittedBedroomsSlots: PropTypes.func.isRequired,
  dispatchSelectDateSlot: PropTypes.func.isRequired,
  dispatchSelectTimeSlot: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchApplyFilters: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchSetDraftAppointment: PropTypes.func.isRequired,
  dispatchReplaceTimeFilters: PropTypes.func.isRequired,
  dispatchReplaceFilters: PropTypes.func.isRequired,
  dispatchSetJourneyState: PropTypes.func.isRequired,
  dispatchSaveTemporaryJourneyState: PropTypes.func.isRequired,
};

FittedBedroomsEditDate.defaultProps = {
  filters: [],
  selectedDateSlot: {
    date: '',
    times: [],
  },
  selectedTimeSlot: {
    eventId: '',
    from: 0,
  },
};
